import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Alert from 'components/global/Alert';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';
import { ModalActions } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import { PAYMENT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';

import { getManageSubscriptionData, setSubscriptionPromoCodeCheck } from '../SubscriptionAccountSlice';
import * as S from '../SubscriptonAccountModal.styled';
import MonthlyFreeIntegratorShipments from './MonthlyFreeIntegratorShipments';

/**
 * @author @muhammedburakak
 */

export default function UpgradeConfirmationModal({
	closeModal,
	nextStep,
	prevStep,
	selectedPackage,
	currentPackage,
	planType
}) {
	// Hooks and states declaration

	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const dispatch = useDispatch();
	const { getLocaleDateWithTime, getDate } = useLocale();
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	const subscriptionDiscountedAmount = useSelector((state) => state.subscriptionAccount.subscriptionDiscountedAmount);
	const user = useSelector((state) => state.user);

	const { manageSubscriptionData, subscriptionPromoCodeCheck } = useSelector((state) => state.subscriptionAccount);

	// Validation schema definition
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					promoCode: yup.number()
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		defaultValues: { promoCode: subscriptionPromoCodeCheck },
		resolver: yupResolver(schema)
	});

	const { watch, getValues } = formMethods;

	useEffect(() => {
		dispatch(getManageSubscriptionData());
	}, []);

	// Promo code validation check
	const checkPromoCode = useCallback(async () => {
		const { promoCode } = getValues();

		if (!promoCode) {
			return;
		}

		await axios
			.post(PAYMENT.CHECK_SUBSCRIPTION_PROMO_CODE, {
				promoCode,
				oldPackageID: currentPackage.id,
				newPackageID: selectedPackage.id,
				paymentType: planType.toUpperCase()
			})
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					if (data.valid === 'true') {
						dispatch(setSubscriptionPromoCodeCheck(promoCode));
						showSuccess({
							message: 'successMsg.validPromoCodeForSubscription'
						});
					} else if (data.valid === 'onhold') {
						showError({
							message: 'errorMsg.onHoldPromoCodeForSubscription'
						});
					} else if (data.valid === 'false') {
						dispatch(setSubscriptionPromoCodeCheck(null));
						showError({
							message: 'errorMsg.invalidPromoCodeForSubscription'
						});
					} else {
						showError();
						AppLogger.error(response);
					}
				} else {
					showError();
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				showError();
				AppLogger.error(error);
			});
	}, []);

	const alertContent = useMemo(() => {
		if (subscriptionDiscountedAmount?.unusedPackageAmount && subscriptionDiscountedAmount?.daysFree) {
			return `${t('upgradeInfoTextWithUnusedPackageAmountAndDaysFree', {
				startDate: getLocaleDateWithTime(manageSubscriptionData?.startDate),
				endDate: getLocaleDateWithTime(manageSubscriptionData?.nextBillingDate),
				currentPackageName: user.accountSuspended
					? 'suspended.packageTitle'
					: t(`${currentPackage.planName}.packageTitle`),
				selectedPackageName: t(`${selectedPackage.planName}.packageTitle`),
				unusedPackageAmount: subscriptionDiscountedAmount?.unusedPackageAmount?.toFixed(2),
				currency: subscriptionDiscountedAmount?.currency,
				daysFree: subscriptionDiscountedAmount?.daysFree,
				amount: subscriptionDiscountedAmount?.amount?.toFixed(2),
				subscriptionAmount: subscriptionDiscountedAmount?.subscriptionAmount
			})}`;
		} else if (subscriptionDiscountedAmount?.unusedPackageAmount) {
			return `${t('upgradeInfoTextWithUnusedPackageAmount', {
				startDate: getLocaleDateWithTime(manageSubscriptionData?.startDate),
				endDate: getLocaleDateWithTime(manageSubscriptionData?.nextBillingDate),
				currentPackageName: user.accountSuspended
					? 'suspended.packageTitle'
					: t(`${currentPackage.planName}.packageTitle`),
				selectedPackageName: t(`${selectedPackage.planName}.packageTitle`),
				unusedPackageAmount: subscriptionDiscountedAmount?.unusedPackageAmount?.toFixed(2),
				currency: subscriptionDiscountedAmount?.currency,
				amount: subscriptionDiscountedAmount?.amount?.toFixed(2)
			})}`;
		} else if (subscriptionDiscountedAmount?.amount === 1) {
			return `${t('upgradeInfoTextAlreadyPaidPackage', {
				startDate: getLocaleDateWithTime(manageSubscriptionData?.startDate),
				endDate: getLocaleDateWithTime(manageSubscriptionData?.nextBillingDate),
				currentPackageName: user.accountSuspended
					? 'suspended.packageTitle'
					: t(`${currentPackage.planName}.packageTitle`),
				selectedPackageName: t(`${selectedPackage.planName}.packageTitle`),
				unusedPackageAmount: subscriptionDiscountedAmount?.unusedPackageAmount?.toFixed(2),
				currency: subscriptionDiscountedAmount?.currency,
				amount: subscriptionDiscountedAmount?.amount?.toFixed(2)
			})}`;
		} else {
			return `${t('upgradeInfoText', {
				startDate: getDate(new Date()),
				endDate: getLocaleDateWithTime(manageSubscriptionData?.nextBillingDate),
				currentPackageName: user.accountSuspended
					? 'suspended.packageTitle'
					: t(`${currentPackage.planName}.packageTitle`),
				selectedPackageName: t(`${selectedPackage.planName}.packageTitle`),
				currency: subscriptionDiscountedAmount?.currency,
				amount: subscriptionDiscountedAmount?.amount?.toFixed(2)
			})}`;
		}
	}, [subscriptionDiscountedAmount]);

	return (
		<>
			<S.ModalContentWrapper>
				<S.StyledForm className="mt-2" formMethods={formMethods}>
					<S.UpgradeConfirmationContainer>
						<S.ImageContainer>
							<Image width="11rem" height="11rem" url={selectedPackage.packageIcon} />
						</S.ImageContainer>
						<S.TextCenterContainer>
							<Typography fontWeight="500" variant="p2" className="mb-3">
								{t('upgradeTitle', {
									currentPackageName: t(`${currentPackage.planName}.packageTitle`),
									selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
								})}
							</Typography>
							<Typography variant="p3" color="var(--content-2)" noWrap>
								<Trans
									i18nKey="upgradePlanSubtitle"
									values={{
										currentPackageName: t(`${currentPackage.planName}.packageTitle`),
										selectedPackageName: t(`${selectedPackage.planName}.packageTitle`)
									}}
									components={{
										b: <strong />
									}}
								/>
							</Typography>
						</S.TextCenterContainer>

						<S.ConfirmationDataBox>
							<div>
								<Typography fontWeight="500" color="var(--content-3)" css={{ width: '150px' }}>
									currentPackage
								</Typography>

								<div>
									<Typography color="var(--content-3)">
										{t(`${currentPackage.planName}.packageTitle`)}
										{!user.accountSuspended && (
											<>
												-
												<b>
													{planType === 'yearly'
														? currentPackage.yearlyPrice
														: currentPackage.monthlyPrice}{' '}
													{currentPackage.currency}
												</b>
												/{planType === 'yearly' ? t('year') : t('month')}
											</>
										)}
									</Typography>
									{currentPackage.monthlyFreeIntegratorOrder && (
										<MonthlyFreeIntegratorShipments pricingPackage={currentPackage} />
									)}
								</div>
							</div>
							<div>
								<Typography fontWeight="500" css={{ width: '150px' }}>
									newPackage
								</Typography>
								<div>
									<Typography>
										{t(`${selectedPackage.planName}.packageTitle`)} -{' '}
										<b>
											{planType === 'yearly'
												? selectedPackage?.yearlyPrice
												: selectedPackage?.monthlyPrice}{' '}
											{selectedPackage.currency}
										</b>
										/{planType === 'yearly' ? t('year') : t('month')}
									</Typography>
									{selectedPackage.monthlyFreeIntegratorOrder && (
										<MonthlyFreeIntegratorShipments pricingPackage={selectedPackage} />
									)}
								</div>
							</div>
						</S.ConfirmationDataBox>
						<Alert className="mt-4 mb-2" isMobile={isMobile} content={alertContent} />

						<S.PromoCodeContainer className="mt-4 mb-2">
							<StringInput name="promoCode" placeholder="promoCodeString" />
							<Button
								disabledTooltip={'errorMsg.enterPromoCodeForSubscription'}
								disabled={!watch('promoCode')}
								onClick={checkPromoCode}
								label="checkCode"
							/>
						</S.PromoCodeContainer>
					</S.UpgradeConfirmationContainer>
				</S.StyledForm>
			</S.ModalContentWrapper>

			<ModalActions className="subscription-modal-actions">
				<Button
					variant="blackOutline"
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
					label="cancel"
					onClick={closeModal}
				/>
				<Button
					variant="outline"
					icon={faAngleLeft}
					label="previous"
					onClick={() => {
						prevStep();
					}}
				/>

				<Button
					variant="blue"
					icon={faAngleRight}
					iconPos="right"
					label="continue"
					onClick={() => {
						nextStep();
					}}
				/>
			</ModalActions>
		</>
	);
}
